import React from 'react';
import { Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  title: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 14,
    color: '#4E73C3',
    marginBottom: 16,
  },
  paragraph: {
    color: '#000',
    lineHeight: 1.5,
    letterSpacing: -0.32,
  },
});

type Props = {
  tableData: {
    date: string;
    dominantPollutant: string;
    NO2: number | string;
    O3: number | string;
    PM25: number | string;
    PM10: number | string;
  }[];
};

const formatPollutant = (pollutant: string) => {
  switch (pollutant) {
    case 'NO2':
      return (
        <Text>
          NO<Text style={{ fontSize: 8 }}>2</Text>
        </Text>
      );
    case 'O3':
      return (
        <Text>
          O<Text style={{ fontSize: 8 }}>3</Text>
        </Text>
      );
    case 'PM2.5':
      return (
        <Text>
          PM<Text style={{ fontSize: 8 }}>2.5</Text>
        </Text>
      );
    case 'PM10':
      return (
        <Text>
          PM<Text style={{ fontSize: 8 }}>10</Text>
        </Text>
      );
    default:
      return <Text>{pollutant}</Text>;
  }
};

const renderFormattedPollutants = (dominantPollutant: string) => {
  if (!dominantPollutant) return null;

  const pollutants = dominantPollutant.split(/, | & /);

  return (
    <Text>
      {pollutants.map((pollutant, index) => (
        <Text key={index}>
          {index > 0 ? (index === pollutants.length - 1 ? ' & ' : ', ') : ''}
          {formatPollutant(pollutant)}
        </Text>
      ))}
    </Text>
  );
};

const SixthPage = ({ tableData }: Props) => {
  const extractDailyMeasurements = () => {
    return tableData.map(
      (
        data: {
          date: string;
          dominantPollutant: string;
          NO2: number | string;
          O3: number | string;
          PM25: number | string;
          PM10: number | string;
        },
        idx: number,
      ) => {
        const { date, dominantPollutant, NO2, O3, PM25, PM10 } = data;
        return (
          <View
            key={date}
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-evenly',
              paddingTop: 2,
              paddingBottom: 2,
              backgroundColor: idx % 2 === 0 ? '#FFF' : '#F7FAFC',
            }}
          >
            <Text style={{ flex: 1, fontSize: 11 }}>{date}</Text>
            <View
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 2,
              }}
            >
              <Text style={{ fontSize: 11, textAlign: 'center' }}>
                {renderFormattedPollutants(dominantPollutant)}
              </Text>
            </View>
            <Text style={{ flex: 1, fontSize: 11 }}>{NO2}</Text>
            <Text style={{ flex: 1, fontSize: 11 }}>{O3}</Text>
            <Text style={{ flex: 1, fontSize: 11 }}>{PM25}</Text>
            <Text style={{ flex: 1, fontSize: 11 }}>{PM10}</Text>
          </View>
        );
      },
    );
  };

  return (
    <View style={{ display: 'flex', flexDirection: 'column' }} wrap={false}>
      <Text style={styles.title}>
        What have the pollutant concentrations been?
      </Text>
      <Text style={{ ...styles.paragraph, fontSize: 11, marginBottom: 16 }}>
        Air quality pollutants are measured as concentrations. This table
        provides a more detailed view of their daily average values. The
        dominant pollutant is based on the highest Air Quality Index (AQI) band
        recorded in the day.{' '}
      </Text>
      <View
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          marginBottom: 4,
          textAlign: 'center',
        }}
      >
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-evenly',
            paddingTop: 4,
            paddingBottom: 4,
            borderBottom: '1px solid #CBD5E0',
          }}
        >
          <Text style={{ flex: 1, fontFamily: 'Helvetica-Bold', fontSize: 11 }}>
            Date
          </Text>
          <Text style={{ flex: 2, fontFamily: 'Helvetica-Bold', fontSize: 11 }}>
            Dominant Pollutant{'\n'}
            <Text style={{ fontFamily: 'Helvetica' }}>AQI Band</Text>
          </Text>
          <Text style={{ flex: 1, fontFamily: 'Helvetica-Bold', fontSize: 11 }}>
            NO<Text style={{ fontSize: 8 }}>2</Text>
            {'\n'}
            <Text style={{ fontFamily: 'Helvetica' }}>(µg/m³)</Text>
          </Text>
          <Text style={{ flex: 1, fontFamily: 'Helvetica-Bold', fontSize: 11 }}>
            O<Text style={{ fontSize: 8 }}>3</Text>
            {'\n'}
            <Text style={{ fontFamily: 'Helvetica' }}>(µg/m³)</Text>
          </Text>
          <Text style={{ flex: 1, fontFamily: 'Helvetica-Bold', fontSize: 11 }}>
            PM<Text style={{ fontSize: 8 }}>2.5</Text>
            {'\n'}
            <Text style={{ fontFamily: 'Helvetica' }}>(µg/m³)</Text>
          </Text>
          <Text style={{ flex: 1, fontFamily: 'Helvetica-Bold', fontSize: 11 }}>
            PM<Text style={{ fontSize: 8 }}>10</Text>
            {'\n'}
            <Text style={{ fontFamily: 'Helvetica' }}>(µg/m³)</Text>
          </Text>
        </View>
        {extractDailyMeasurements()}
      </View>
    </View>
  );
};

export default SixthPage;
